import { useState, type FormEvent } from 'react'
import { phoneCountries } from '../data/phoneCountries'
import { demoRequestInbox } from '../data/requestDemo'
import { trackDemoRequest } from '../utils/analytics'

export type DemoField = 'name' | 'email' | 'phone' | 'businessName' | 'businessType' | 'message'
export type DemoValues = Record<DemoField, string> & { country: string }
export type DemoErrors = Partial<Record<DemoField, string>>
export type DemoStatus = 'idle' | 'submitting' | 'success'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
const FIELD_ORDER: DemoField[] = ['name', 'email', 'phone', 'businessName', 'businessType']

export function isValidEmail(value: string): boolean {
  return EMAIL_RE.test(value.trim())
}

function validate(field: DemoField, values: DemoValues): string | undefined {
  const value = values[field].trim()
  switch (field) {
    case 'name':
      return value ? undefined : 'Enter your full name'
    case 'email':
      if (!value) return 'Enter your email'
      return isValidEmail(value) ? undefined : 'Enter a valid email address, like name@business.rw'
    case 'phone': {
      if (!value) return 'Enter your phone number'
      const digits = value.replace(/\D/g, '')
      const valid = values.country === 'RW' ? /^0?7[2389]\d{7}$/.test(digits) : digits.length >= 7 && digits.length <= 12
      return valid ? undefined : 'Enter a valid phone number'
    }
    case 'businessName':
      return value ? undefined : 'Enter your business name'
    case 'businessType':
      return value ? undefined : 'Choose your business type'
    default:
      return undefined
  }
}

export function useRequestDemoForm(initialEmail: string) {
  const [values, setValues] = useState<DemoValues>({
    name: '',
    email: initialEmail,
    phone: '',
    country: 'RW',
    businessName: '',
    businessType: '',
    message: '',
  })
  const [errors, setErrors] = useState<DemoErrors>({})
  const [touched, setTouched] = useState<Partial<Record<DemoField, boolean>>>({})
  const [status, setStatus] = useState<DemoStatus>('idle')
  const [sendError, setSendError] = useState('')

  function setValue(field: DemoField | 'country', value: string) {
    setValues((prev) => {
      const next = { ...prev, [field]: value }
      const target: DemoField = field === 'country' ? 'phone' : field
      if (touched[target]) setErrors((e) => ({ ...e, [target]: validate(target, next) }))
      return next
    })
  }

  function blur(field: DemoField) {
    setTouched((t) => ({ ...t, [field]: true }))
    setErrors((e) => ({ ...e, [field]: validate(field, values) }))
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const nextErrors: DemoErrors = {}
    FIELD_ORDER.forEach((field) => {
      const error = validate(field, values)
      if (error) nextErrors[field] = error
    })
    setErrors(nextErrors)
    setTouched({ name: true, email: true, phone: true, businessName: true, businessType: true })

    const firstInvalid = FIELD_ORDER.find((field) => nextErrors[field])
    if (firstInvalid) {
      document.getElementById(`demo-${firstInvalid}`)?.focus()
      return
    }

    void send()
  }

  async function send() {
    setStatus('submitting')
    setSendError('')
    const dial = phoneCountries.find((c) => c.code === values.country)?.dial ?? ''
    try {
      const response = await fetch(`https://formsubmit.co/ajax/${demoRequestInbox}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          _subject: `New demo request: ${values.businessName.trim()}`,
          _template: 'table',
          _captcha: 'false',
          _replyto: values.email.trim(),
          'Full name': values.name.trim(),
          Email: values.email.trim(),
          Phone: `${dial} ${values.phone.trim()}`,
          'Business name': values.businessName.trim(),
          'Business type': values.businessType,
          Message: values.message.trim() || '—',
        }),
      })
      if (!response.ok) throw new Error(`Request failed with ${response.status}`)
      trackDemoRequest(values.businessType)
      setStatus('success')
    } catch {
      setStatus('idle')
      setSendError(`We couldn’t send your request. Check your connection and try again, or email ${demoRequestInbox}.`)
    }
  }

  return { values, errors, status, sendError, setValue, blur, submit }
}
